import React, { useState } from 'react';
import { toast } from 'sonner';
import { Download, Share2, Printer, BarChart3, Activity } from 'lucide-react';

const Performance = () => {
  // Main tab state
  const [activeMainTab, setActiveMainTab] = useState('performance');
  
  // Performance tab state
  const [shotFilter, setShotFilter] = useState('all');

  // Statistics tab state
  const [selectedSide, setSelectedSide] = useState('bos');
  const [groupBy, setGroupBy] = useState('overall');
  const [statType, setStatType] = useState('averages');
  const [selectedAthletes, setSelectedAthletes] = useState(['all', 'jayson-tatum']);

  // Shot stats data
  const shotStats = [
    { player: 'Jayson Tatum', fga: 87, fgm: 61, fgPct: '70.1%', threePA: 37, threePM: 11, threePPct: '29.9%' },
    { player: 'Jaylen Brown', fga: 54, fgm: 27, fgPct: '50.0%', threePA: 21, threePM: 7, threePPct: '33.3%' },
    { player: 'Derrick White', fga: 48, fgm: 28, fgPct: '58.3%', threePA: 18, threePM: 2, threePPct: '11.1%' },
  ];

  // Shot timeline data
  const shotTimeline = [
    { time: '08:00', shots: [{ type: 'make' }, { type: 'miss' }] },
    { time: '08:10', shots: [{ type: 'make' }] },
    { time: '08:20', shots: [{ type: 'make' }, { type: 'miss' }] },
    { time: '08:30', shots: [{ type: 'make' }, { type: 'make' }] },
    { time: '08:45', shots: [{ type: 'make' }, { type: 'miss' }, { type: 'make' }] },
    { time: '09:00', shots: [{ type: 'make' }, { type: 'make' }] },
    { time: '09:05', shots: [{ type: 'miss' }, { type: 'make' }, { type: 'make' }] },
    { time: '09:15', shots: [{ type: 'make' }, { type: 'make' }] },
    { time: '09:30', shots: [{ type: 'make' }, { type: 'miss' }] },
  ];

  // Shot map data
  const shotMapData = [
    { x: 30, y: 25, type: 'miss' },
    { x: 45, y: 35, type: 'make' },
    { x: 55, y: 40, type: 'make' },
    { x: 48, y: 45, type: 'make' },
    { x: 52, y: 42, type: 'make' },
    { x: 60, y: 38, type: 'make' },
    { x: 70, y: 30, type: 'make' },
    { x: 75, y: 25, type: 'make' },
    { x: 25, y: 55, type: 'miss' },
    { x: 35, y: 65, type: 'make' },
    { x: 45, y: 75, type: 'miss' },
    { x: 55, y: 72, type: 'miss' },
    { x: 65, y: 70, type: 'make' },
    { x: 75, y: 60, type: 'miss' },
    { x: 50, y: 85, type: 'make' },
  ];

  // Statistics data
  const team = {
    name: 'Boston Celtics',
    games: 24,
    athletes: 'All Athletes',
    statType: 'Averages'
  };

  const fourFactorsData = {
    season: { games: '2024-2025 Season', gp: 24, efg: 52.3, to: 12.5, oreb: 28.3, dreb: 71.2, ftf: 0.24, vps: 1.08 },
    periods: [
      { period: '1st', gp: 24, efg: 51.8, to: 13.1, oreb: 27.5, dreb: 70.8, ftf: 0.23, vps: 1.06 },
      { period: '2nd', gp: 24, efg: 53.2, to: 12.8, oreb: 29.1, dreb: 71.6, ftf: 0.25, vps: 1.09 },
      { period: '3rd', gp: 24, efg: 52.1, to: 11.9, oreb: 28.7, dreb: 71.5, ftf: 0.24, vps: 1.08 },
      { period: '4th', gp: 24, efg: 52.9, to: 12.3, oreb: 28, dreb: 71, ftf: 0.24, vps: 1.09 },
    ]
  };

  const shootingData = {
    season: { games: '2024-2025 Season', gp: 24, fgm: 38.2, fga: 82.5, fg: 46.3, efg: 52.3, twofgm: 28.1, twofga: 58.3, twofg: 48.2, threefgm: 10.1, threefga: 24.2, threefg: 41.7 },
    periods: [
      { period: '1st', gp: 24, fgm: 9.5, fga: 20.6, fg: 46.1, efg: 51.8, twofgm: 7, twofga: 14.6, twofg: 47.9, threefgm: 2.5, threefga: 6, threefg: 41.7 },
      { period: '2nd', gp: 24, fgm: 9.8, fga: 20.8, fg: 47.1, efg: 53.2, twofgm: 7.2, twofga: 14.7, twofg: 49, threefgm: 2.6, threefga: 6.1, threefg: 42.6 },
      { period: '3rd', gp: 24, fgm: 9.4, fga: 20.4, fg: 46.1, efg: 52.1, twofgm: 7, twofga: 14.5, twofg: 48.3, threefgm: 2.4, threefga: 5.9, threefg: 40.7 },
      { period: '4th', gp: 24, fgm: 9.5, fga: 20.7, fg: 45.9, efg: 52.9, twofgm: 6.9, twofga: 14.5, twofg: 47.6, threefgm: 2.6, threefga: 6.2, threefg: 41.9 },
    ]
  };

  const freeThrowsData = {
    season: { games: '2024-2025 Season', gp: 24, ftm: 15.8, fta: 19.2, ft: 82.3, pts: 112.4, ppg: 112.4, ppp: 1.12 },
    periods: [
      { period: '1st', gp: 24, ftm: 4.2, fta: 5.1, ft: 82.4, pts: 28.7, ppg: 28.7, ppp: 1.14 },
      { period: '2nd', gp: 24, ftm: 3.8, fta: 4.6, ft: 82.6, pts: 28.2, ppg: 28.2, ppp: 1.11 },
      { period: '3rd', gp: 24, ftm: 3.9, fta: 4.7, ft: 83.0, pts: 27.8, ppg: 27.8, ppp: 1.10 },
      { period: '4th', gp: 24, ftm: 3.9, fta: 4.8, ft: 81.3, pts: 27.7, ppg: 27.7, ppp: 1.13 },
    ]
  };

  const athletes = [
    { id: 'all', name: 'All' },
    { id: 'jayson-tatum', name: 'Jayson Tatum' },
    { id: 'jaylen-brown', name: 'Jaylen Brown' },
    { id: 'derrick-white', name: 'Derrick White' },
    { id: 'jrue-holiday', name: 'Jrue Holiday' },
  ];

  const toggleAthlete = (id) => {
    if (id === 'all') {
      setSelectedAthletes(['all']);
    } else {
      setSelectedAthletes(prev => {
        const filtered = prev.filter(a => a !== 'all');
        if (filtered.includes(id)) {
          return filtered.filter(a => a !== id);
        }
        return [...filtered, id];
      });
    }
  };

  const filteredShots = shotMapData.filter(shot => {
    if (shotFilter === 'all') return true;
    if (shotFilter === 'makes') return shot.type === 'make';
    if (shotFilter === 'misses') return shot.type === 'miss';
    return true;
  });

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header with Tabs */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">Performance & Statistics</h1>
          <p className="text-gray-400 mt-1">Shot analysis, player statistics and team metrics</p>
          
          {/* Main Tabs */}
          <div className="flex gap-2 mt-4 border-b border-gray-700 pb-0">
            <button
              onClick={() => setActiveMainTab('performance')}
              data-testid="performance-tab"
              className={`px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-[2px] ${
                activeMainTab === 'performance'
                  ? 'text-green-400 border-green-400'
                  : 'text-gray-400 border-transparent hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <Activity size={16} />
                Performance
              </div>
            </button>
            <button
              onClick={() => setActiveMainTab('statistics')}
              data-testid="statistics-tab"
              className={`px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-[2px] ${
                activeMainTab === 'statistics'
                  ? 'text-blue-400 border-blue-400'
                  : 'text-gray-400 border-transparent hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 size={16} />
                Statistics
              </div>
            </button>
          </div>
        </div>

        {/* PERFORMANCE TAB */}
        {activeMainTab === 'performance' && (
          <>
            {/* Shot Stats Table */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6 mb-6">
              <h2 className="text-white font-semibold text-lg mb-4">Shot Stats</h2>
              <table className="w-full">
                <thead>
                  <tr className="text-gray-400 text-sm">
                    <th className="text-left py-3 px-4">Player</th>
                    <th className="text-left py-3 px-4">FGA</th>
                    <th className="text-left py-3 px-4">FGM</th>
                    <th className="text-left py-3 px-4">FG%</th>
                    <th className="text-left py-3 px-4">3PA</th>
                    <th className="text-left py-3 px-4">3PM</th>
                    <th className="text-left py-3 px-4">3P%</th>
                  </tr>
                </thead>
                <tbody>
                  {shotStats.map((player, idx) => (
                    <tr key={idx} className="border-t border-gray-800">
                      <td className="py-4 px-4 text-green-400 font-medium">{player.player}</td>
                      <td className="py-4 px-4 text-white">{player.fga}</td>
                      <td className="py-4 px-4 text-white">{player.fgm}</td>
                      <td className="py-4 px-4 text-green-400">{player.fgPct}</td>
                      <td className="py-4 px-4 text-white">{player.threePA}</td>
                      <td className="py-4 px-4 text-white">{player.threePM}</td>
                      <td className="py-4 px-4 text-green-400">{player.threePPct}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Shot Timeline */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6 mb-6">
              <h2 className="text-white font-semibold text-lg mb-6">Shot Timeline</h2>
              <div className="relative">
                <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-700 -translate-y-1/2"></div>
                <div className="flex justify-between relative">
                  {shotTimeline.map((point, idx) => (
                    <div key={idx} className="flex flex-col items-center">
                      <div className="flex gap-1 mb-2">
                        {point.shots.map((shot, shotIdx) => (
                          <div 
                            key={shotIdx}
                            className={`w-3 h-3 rounded-full ${shot.type === 'make' ? 'bg-green-500' : 'bg-red-500'}`}
                          />
                        ))}
                      </div>
                      <span className="text-gray-500 text-sm mt-4">{point.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Row - Shot Map & Shot Area */}
            <div className="grid grid-cols-2 gap-6">
              {/* Shot Map */}
              <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white font-semibold text-lg">Shot Map</h2>
                  <div className="flex gap-2">
                    {['all', 'makes', 'misses'].map(filter => (
                      <button
                        key={filter}
                        onClick={() => setShotFilter(filter)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                          shotFilter === filter 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative bg-[#0f172a] rounded-lg overflow-hidden" style={{ aspectRatio: '1.2' }}>
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <rect x="5" y="5" width="90" height="90" fill="none" stroke="#374151" strokeWidth="0.5" />
                    <path d="M 15 95 Q 15 30 50 25 Q 85 30 85 95" fill="none" stroke="#374151" strokeWidth="0.5" />
                    <rect x="30" y="70" width="40" height="25" fill="none" stroke="#374151" strokeWidth="0.5" />
                    <circle cx="50" cy="70" r="10" fill="none" stroke="#374151" strokeWidth="0.5" />
                    <rect x="45" y="92" width="10" height="3" fill="#374151" />
                    <circle cx="50" cy="88" r="3" fill="none" stroke="#374151" strokeWidth="0.5" />
                    {filteredShots.map((shot, idx) => (
                      <circle
                        key={idx}
                        cx={shot.x}
                        cy={shot.y}
                        r="2.5"
                        fill={shot.type === 'make' ? '#22c55e' : '#ef4444'}
                        className="cursor-pointer hover:r-3 transition-all"
                      />
                    ))}
                  </svg>
                </div>
              </div>

              {/* Shot Area */}
              <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6">
                <h2 className="text-white font-semibold text-lg mb-4">Shot Area</h2>
                
                <div className="relative bg-[#0f172a] rounded-lg overflow-hidden" style={{ aspectRatio: '1.2' }}>
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <path d="M 5 5 L 95 5 L 95 95 L 5 95 Z" fill="none" stroke="#374151" strokeWidth="0.3" />
                    <path d="M 10 95 Q 10 25 50 15 Q 90 25 90 95" fill="none" stroke="#374151" strokeWidth="0.3" />

                    <rect x="5" y="15" width="12" height="30" fill="#3b82f6" fillOpacity="0.6" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="11" y="28" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">20%</text>
                    <text x="11" y="35" textAnchor="middle" fill="white" fontSize="3">3PT Left</text>
                    <text x="11" y="39" textAnchor="middle" fill="white" fontSize="3">Corner</text>

                    <rect x="83" y="15" width="12" height="30" fill="#3b82f6" fillOpacity="0.4" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="89" y="28" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">14%</text>
                    <text x="89" y="35" textAnchor="middle" fill="white" fontSize="3">3PT Right</text>
                    <text x="89" y="39" textAnchor="middle" fill="white" fontSize="3">Corner</text>

                    <rect x="25" y="25" width="15" height="25" fill="#3b82f6" fillOpacity="0.7" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="32" y="37" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">63%</text>
                    <text x="32" y="44" textAnchor="middle" fill="white" fontSize="3">Paint</text>
                    <text x="32" y="48" textAnchor="middle" fill="white" fontSize="3">Left</text>

                    <rect x="40" y="25" width="20" height="25" fill="#3b82f6" fillOpacity="0.95" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="50" y="37" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">93%</text>
                    <text x="50" y="44" textAnchor="middle" fill="white" fontSize="3">Paint Center</text>

                    <rect x="60" y="25" width="15" height="25" fill="#3b82f6" fillOpacity="0.75" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="67" y="37" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">68%</text>
                    <text x="67" y="44" textAnchor="middle" fill="white" fontSize="3">Paint</text>
                    <text x="67" y="48" textAnchor="middle" fill="white" fontSize="3">Right</text>

                    <rect x="20" y="50" width="20" height="18" fill="#60a5fa" fillOpacity="0.6" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="30" y="60" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">54%</text>
                    <text x="30" y="66" textAnchor="middle" fill="white" fontSize="3">Mid Left</text>

                    <rect x="60" y="50" width="20" height="18" fill="#60a5fa" fillOpacity="0.55" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="70" y="60" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">49%</text>
                    <text x="70" y="66" textAnchor="middle" fill="white" fontSize="3">Mid Right</text>

                    <rect x="5" y="50" width="15" height="22" fill="#60a5fa" fillOpacity="0.45" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="12" y="62" textAnchor="middle" fill="white" fontSize="5" fontWeight="bold">38%</text>
                    <text x="12" y="68" textAnchor="middle" fill="white" fontSize="2.5">3PT Left</text>
                    <text x="12" y="71" textAnchor="middle" fill="white" fontSize="2.5">Wing</text>

                    <rect x="80" y="50" width="15" height="22" fill="#60a5fa" fillOpacity="0.35" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="87" y="62" textAnchor="middle" fill="white" fontSize="5" fontWeight="bold">28%</text>
                    <text x="87" y="68" textAnchor="middle" fill="white" fontSize="2.5">3PT Right</text>
                    <text x="87" y="71" textAnchor="middle" fill="white" fontSize="2.5">Wing</text>

                    <rect x="35" y="72" width="30" height="15" fill="#60a5fa" fillOpacity="0.4" stroke="#60a5fa" strokeWidth="0.3" />
                    <text x="50" y="81" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">34%</text>
                    <text x="50" y="86" textAnchor="middle" fill="white" fontSize="3">3PT Top</text>
                  </svg>
                </div>

                <div className="mt-4">
                  <div className="flex justify-between text-gray-400 text-sm mb-2">
                    <span>0%</span>
                    <span>Shooting Efficiency</span>
                    <span>100%</span>
                  </div>
                  <div className="h-3 rounded-full overflow-hidden flex">
                    <div className="bg-blue-900 flex-1"></div>
                    <div className="bg-blue-700 flex-1"></div>
                    <div className="bg-blue-500 flex-1"></div>
                    <div className="bg-blue-400 flex-1"></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* STATISTICS TAB */}
        {activeMainTab === 'statistics' && (
          <>
            {/* Stats Header Actions */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-gray-400">
                  {team.name} • {team.games} Games • {team.athletes} — {team.statType}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => toast.info('Exporting stats...')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  <Download size={18} />
                  Export
                </button>
                <button 
                  onClick={() => toast.info('Share link copied!')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  <Share2 size={18} />
                  Share
                </button>
                <button 
                  onClick={() => toast.info('Opening print dialog...')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  <Printer size={18} />
                  Print
                </button>
              </div>
            </div>

            <div className="flex gap-6">
              {/* Left Sidebar - Filters */}
              <div className="w-72 flex-shrink-0 space-y-4">
                {/* Team */}
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
                  <h3 className="text-white font-semibold mb-3">TEAM</h3>
                  <p className="text-white mb-2">{team.name}</p>
                  <button className="text-green-400 text-sm hover:underline">Scout a Different Team</button>
                </div>

                {/* Games */}
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
                  <h3 className="text-white font-semibold mb-3">GAMES</h3>
                  <p className="text-gray-400 text-sm mb-2">Viewing stats from {team.games} games</p>
                  <button className="text-green-400 text-sm hover:underline">View Other Games</button>
                </div>

                {/* Side */}
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
                  <h3 className="text-white font-semibold mb-3">SIDE</h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="side"
                        checked={selectedSide === 'bos'}
                        onChange={() => setSelectedSide('bos')}
                        className="w-4 h-4 accent-blue-500"
                      />
                      <span className="text-white">BOS (Celtics)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="side"
                        checked={selectedSide === 'opponents'}
                        onChange={() => setSelectedSide('opponents')}
                        className="w-4 h-4 accent-blue-500"
                      />
                      <span className="text-white">Opponents</span>
                    </label>
                  </div>
                </div>

                {/* Group By */}
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
                  <h3 className="text-white font-semibold mb-3">GROUP BY</h3>
                  <div className="space-y-2">
                    {['overall', 'game', 'athlete', 'lineup'].map(option => (
                      <label key={option} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="groupBy"
                          checked={groupBy === option}
                          onChange={() => setGroupBy(option)}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-white capitalize">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Stat Type */}
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
                  <h3 className="text-white font-semibold mb-3">STAT TYPE</h3>
                  <div className="space-y-2">
                    {['averages', 'totals'].map(option => (
                      <label key={option} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="statType"
                          checked={statType === option}
                          onChange={() => setStatType(option)}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-white capitalize">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Athletes */}
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
                  <h3 className="text-white font-semibold mb-3">ATHLETES</h3>
                  <div className="space-y-2">
                    {athletes.map(athlete => (
                      <label key={athlete.id} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAthletes.includes(athlete.id)}
                          onChange={() => toggleAthlete(athlete.id)}
                          className="w-4 h-4 accent-blue-500"
                        />
                        <span className="text-white">{athlete.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Main Content - Stats Tables */}
              <div className="flex-1 space-y-6">
                {/* Four Factors */}
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6">
                  <h2 className="text-white font-semibold text-lg mb-4">Four Factors</h2>
                  <table className="w-full">
                    <thead>
                      <tr className="text-gray-400 text-sm">
                        <th className="text-left py-3 px-4">Games</th>
                        <th className="text-left py-3 px-4">GP</th>
                        <th className="text-left py-3 px-4">eFG%</th>
                        <th className="text-left py-3 px-4">TO%</th>
                        <th className="text-left py-3 px-4">OREB%</th>
                        <th className="text-left py-3 px-4">DREB%</th>
                        <th className="text-left py-3 px-4">FTF</th>
                        <th className="text-left py-3 px-4">VPS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-gray-800">
                        <td className="py-4 px-4 text-white">{fourFactorsData.season.games}</td>
                        <td className="py-4 px-4 text-white">{fourFactorsData.season.gp}</td>
                        <td className="py-4 px-4 text-white">{fourFactorsData.season.efg}</td>
                        <td className="py-4 px-4 text-white">{fourFactorsData.season.to}</td>
                        <td className="py-4 px-4 text-white">{fourFactorsData.season.oreb}</td>
                        <td className="py-4 px-4 text-white">{fourFactorsData.season.dreb}</td>
                        <td className="py-4 px-4 text-white">{fourFactorsData.season.ftf}</td>
                        <td className="py-4 px-4 text-white">{fourFactorsData.season.vps}</td>
                      </tr>
                      <tr>
                        <td colSpan="8" className="py-2 px-4 text-gray-500 text-sm uppercase">By Period</td>
                      </tr>
                      {fourFactorsData.periods.map((period, idx) => (
                        <tr key={idx} className="border-t border-gray-800/50">
                          <td className="py-3 px-4 text-gray-400">{period.period}</td>
                          <td className="py-3 px-4 text-white">{period.gp}</td>
                          <td className="py-3 px-4 text-white">{period.efg}</td>
                          <td className="py-3 px-4 text-white">{period.to}</td>
                          <td className="py-3 px-4 text-white">{period.oreb}</td>
                          <td className="py-3 px-4 text-white">{period.dreb}</td>
                          <td className="py-3 px-4 text-white">{period.ftf}</td>
                          <td className="py-3 px-4 text-white">{period.vps}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Shooting */}
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6">
                  <h2 className="text-white font-semibold text-lg mb-4">Shooting • Two Pointers • Three Pointers</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px]">
                      <thead>
                        <tr className="text-gray-400 text-sm">
                          <th className="text-left py-3 px-3">Games</th>
                          <th className="text-left py-3 px-3">GP</th>
                          <th className="text-left py-3 px-3 bg-gray-800/50">FGM</th>
                          <th className="text-left py-3 px-3">FGA</th>
                          <th className="text-left py-3 px-3">FG%</th>
                          <th className="text-left py-3 px-3">eFG%</th>
                          <th className="text-left py-3 px-3">2FGM</th>
                          <th className="text-left py-3 px-3">2FGA</th>
                          <th className="text-left py-3 px-3">2FG%</th>
                          <th className="text-left py-3 px-3 bg-green-900/30">3FGM</th>
                          <th className="text-left py-3 px-3">3FGA</th>
                          <th className="text-left py-3 px-3">3FG%</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-gray-800">
                          <td className="py-4 px-3 text-white">{shootingData.season.games}</td>
                          <td className="py-4 px-3 text-white">{shootingData.season.gp}</td>
                          <td className="py-4 px-3 text-white bg-gray-800/30">{shootingData.season.fgm}</td>
                          <td className="py-4 px-3 text-white">{shootingData.season.fga}</td>
                          <td className="py-4 px-3 text-white">{shootingData.season.fg}</td>
                          <td className="py-4 px-3 text-white">{shootingData.season.efg}</td>
                          <td className="py-4 px-3 text-white">{shootingData.season.twofgm}</td>
                          <td className="py-4 px-3 text-white">{shootingData.season.twofga}</td>
                          <td className="py-4 px-3 text-white">{shootingData.season.twofg}</td>
                          <td className="py-4 px-3 text-white bg-green-900/20">{shootingData.season.threefgm}</td>
                          <td className="py-4 px-3 text-white">{shootingData.season.threefga}</td>
                          <td className="py-4 px-3 text-white">{shootingData.season.threefg}</td>
                        </tr>
                        <tr>
                          <td colSpan="12" className="py-2 px-3 text-gray-500 text-sm uppercase">By Period</td>
                        </tr>
                        {shootingData.periods.map((period, idx) => (
                          <tr key={idx} className="border-t border-gray-800/50">
                            <td className="py-3 px-3 text-gray-400">{period.period}</td>
                            <td className="py-3 px-3 text-white">{period.gp}</td>
                            <td className="py-3 px-3 text-white bg-gray-800/20">{period.fgm}</td>
                            <td className="py-3 px-3 text-white">{period.fga}</td>
                            <td className="py-3 px-3 text-white">{period.fg}</td>
                            <td className="py-3 px-3 text-white">{period.efg}</td>
                            <td className="py-3 px-3 text-white">{period.twofgm}</td>
                            <td className="py-3 px-3 text-white">{period.twofga}</td>
                            <td className="py-3 px-3 text-white">{period.twofg}</td>
                            <td className="py-3 px-3 text-white bg-green-900/10">{period.threefgm}</td>
                            <td className="py-3 px-3 text-white">{period.threefga}</td>
                            <td className="py-3 px-3 text-white">{period.threefg}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Free Throws & Scoring */}
                <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6">
                  <h2 className="text-white font-semibold text-lg mb-4">Free Throws • Scoring • Advanced Scoring</h2>
                  <table className="w-full">
                    <thead>
                      <tr className="text-gray-400 text-sm">
                        <th className="text-left py-3 px-4">Games</th>
                        <th className="text-left py-3 px-4">GP</th>
                        <th className="text-left py-3 px-4">FTM</th>
                        <th className="text-left py-3 px-4">FTA</th>
                        <th className="text-left py-3 px-4">FT%</th>
                        <th className="text-left py-3 px-4">PTS</th>
                        <th className="text-left py-3 px-4">PPG</th>
                        <th className="text-left py-3 px-4">PPP</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-gray-800">
                        <td className="py-4 px-4 text-white">{freeThrowsData.season.games}</td>
                        <td className="py-4 px-4 text-white">{freeThrowsData.season.gp}</td>
                        <td className="py-4 px-4 text-white">{freeThrowsData.season.ftm}</td>
                        <td className="py-4 px-4 text-white">{freeThrowsData.season.fta}</td>
                        <td className="py-4 px-4 text-white">{freeThrowsData.season.ft}</td>
                        <td className="py-4 px-4 text-white">{freeThrowsData.season.pts}</td>
                        <td className="py-4 px-4 text-white">{freeThrowsData.season.ppg}</td>
                        <td className="py-4 px-4 text-white">{freeThrowsData.season.ppp}</td>
                      </tr>
                      <tr>
                        <td colSpan="8" className="py-2 px-4 text-gray-500 text-sm uppercase">By Period</td>
                      </tr>
                      {freeThrowsData.periods.map((period, idx) => (
                        <tr key={idx} className="border-t border-gray-800/50">
                          <td className="py-3 px-4 text-gray-400">{period.period}</td>
                          <td className="py-3 px-4 text-white">{period.gp}</td>
                          <td className="py-3 px-4 text-white">{period.ftm}</td>
                          <td className="py-3 px-4 text-white">{period.fta}</td>
                          <td className="py-3 px-4 text-white">{period.ft}</td>
                          <td className="py-3 px-4 text-white">{period.pts}</td>
                          <td className="py-3 px-4 text-white">{period.ppg}</td>
                          <td className="py-3 px-4 text-white">{period.ppp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Performance;
